/**
 * What the connected ad account holds, as figures — slice 41's read, drawn.
 *
 * ## IT RENDERS A STATE, NOT A LIST
 *
 * The prop is `MetaAdsReadState`, and every arm draws differently. That is the
 * point of the union the API sends: `not_checked` and `not_permitted` carry no
 * campaign list at all, so this component has nothing to draw as "this account
 * is empty" when the truth is "this token cannot read it". The two refusals
 * also draw differently from each other — one asks for a Test, the other for
 * App Review.
 *
 * ## FIGURES, CHIPS AND BARS — NO SENTENCES
 *
 * The rule for this programme. The one exception is `failed`, whose message is
 * the repair: a refusal that does not say what to fix is a dead end.
 *
 * ## THE THREE THINGS ON THIS CARD THAT ARE EASY TO DRAW WRONG
 *
 *   - **Reach is the account's own figure.** It comes from Meta's unbroken
 *     call, and the per-placement reaches beside it are drawn as numbers with
 *     **no bar**: a bar invites the reader to add them, and they do not add —
 *     somebody who saw two placements is one person, not two. Spend *is*
 *     additive, so spend gets the bars.
 *   - **Money is basic units until the last moment.** `formatMetaMoney` divides
 *     by Meta's offset for the currency and hands the result to `Intl` in that
 *     currency. A currency with no known offset draws as a chip, not as a
 *     number that might be a hundred times wrong.
 *   - **A spend cap of `null` is no cap.** It draws as a badge saying so, never
 *     as a bar against zero.
 *
 * ## WHY IT IS ITS OWN FILE
 *
 * `MetaAdsCard` reaches the API client, and so `import.meta.env`, which a
 * verify script cannot load. This file imports only contracts and the design
 * system, so `verify-meta-ads-tab` renders it directly — the arrangement
 * `MetaAdsAccountState` already uses for the connection facts.
 */
import { Badge, Kicker, ProportionBar, StateChip, cn } from '@wend/ui';
import type { ReactNode } from 'react';

/* `@wend/shared` directly rather than through the API client, which reaches
   `import.meta.env` — see `meta-ads-reading.ts` for the run that found out. */
import {
  metaCurrencyOffset,
  type MetaAdsAccountRead,
  type MetaAdsFigures,
  type MetaAdsReadState,
  type MetaDeliveryStatus,
} from '@wend/shared';

import { formatCount } from '../../lib/format';
import { placementLabel } from '../../lib/surface-display';

/**
 * Basic units → a currency string, or `null` when this product will not guess
 * the currency's offset.
 *
 * The division happens here, at the last moment, because this is display: the
 * figure has been an exact integer all the way from Meta's string to this line.
 */
export function formatMetaMoney(minor: number, currency: string): string | null {
  const offset = metaCurrencyOffset(currency);
  if (offset === null) return null;
  const digits = Math.round(Math.log10(offset));
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(minor / offset);
}

/* Slice 44: each placement by its surface's label — "Facebook ads", not
   "Facebook" — from the one place every screen takes a surface's name. */
const PLACEMENT_LABEL = placementLabel;

const STATUS_LABEL: Readonly<Record<MetaDeliveryStatus, string>> = {
  active: 'active',
  paused: 'paused',
  in_review: 'in review',
  rejected: 'rejected',
  billing: 'billing',
  issues: 'with issues',
  archived: 'archived',
  deleted: 'deleted',
  unknown: 'unread',
};

/** Only a live delivery earns green; everything that cannot spend today is warn. */
const STATUS_TONE: Readonly<Record<MetaDeliveryStatus, 'good' | 'neutral' | 'warn' | 'bad'>> = {
  active: 'good',
  paused: 'neutral',
  in_review: 'neutral',
  rejected: 'bad',
  billing: 'bad',
  issues: 'warn',
  archived: 'neutral',
  deleted: 'neutral',
  unknown: 'warn',
};

/* Slice 43: a published draft's campaign and ads read in the same words and
   colours as the account's campaigns. */
export { STATUS_LABEL as DELIVERY_STATUS_LABEL, STATUS_TONE as DELIVERY_STATUS_TONE };

export function MetaAdsAccountRead({ state }: { state: MetaAdsReadState }) {
  switch (state.state) {
    /* The card draws the connect form instead. Nothing to add here. */
    case 'not_connected':
      return null;

    case 'not_checked':
      return (
        <div data-role="meta-ads-read" data-state="not_checked">
          <StateChip state="not_measured" label="Not checked" />
        </div>
      );

    case 'not_permitted':
      return (
        <div data-role="meta-ads-read" data-state="not_permitted">
          <Badge tone="bad">No ads permission</Badge>
        </div>
      );

    case 'failed':
      return (
        <p
          data-role="meta-ads-read"
          data-state="failed"
          className="max-w-prose text-[0.88rem] leading-relaxed text-bad"
        >
          {state.failure.message}
        </p>
      );

    case 'ok':
      return <AccountFigures read={state.read} />;

    default: {
      const exhaustive: never = state;
      return exhaustive;
    }
  }
}

/* -------------------------------------------------------------------------- */
/* The ok state                                                                */
/* -------------------------------------------------------------------------- */

function AccountFigures({ read }: { read: MetaAdsAccountRead }) {
  const money = (minor: number | null): ReactNode => {
    if (minor === null) return <StateChip state="not_measured" label="Not measured" />;
    return formatMetaMoney(minor, read.currency) ?? <StateChip state="not_measured" label={read.currency} />;
  };
  const count = (value: number | null): ReactNode =>
    value === null ? <StateChip state="not_measured" label="Not measured" /> : formatCount(value);

  const spendTotal = read.figures.spend;
  const statusCounts = countStatuses(read.campaigns.map((one) => one.status));

  return (
    <section data-role="meta-ads-read" data-state="ok" className="flex flex-col gap-5 border-t border-line pt-5">
      {/*
        The window's tag: its length and the zone it was counted in, in the
        Overview's tag style. The zone is the account's, and saying which is the
        whole defence against a reader supplying their own.
      */}
      <div className="flex flex-wrap items-center gap-2">
        <span
          data-role="meta-ads-window"
          className="font-mono text-[0.66rem] uppercase tracking-[0.14em] text-muted"
        >
          Last {read.window.days} days · {read.window.timezone}
        </span>
        {read.transport === 'stub' && <StateChip state="estimated" label="Simulated" />}
        {read.truncated && <StateChip state="under_floor" label="Partial list" />}
      </div>

      <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Figure label="Spend" kind="money">{money(read.figures.spend)}</Figure>
        <Figure label="Impressions">{count(read.figures.impressions)}</Figure>
        {/* The account's own reach — overlap removed by Meta, never summed here. */}
        <Figure label="People reached">{count(read.figures.reach)}</Figure>
        <Figure label="Clicks">{count(read.figures.clicks)}</Figure>
      </dl>

      <ul data-role="meta-ads-placements" className="flex flex-col gap-3">
        {read.byPlacement.map((row) => (
          <PlacementRow
            key={row.placement}
            label={PLACEMENT_LABEL(row.placement)}
            figures={row.figures}
            spendTotal={spendTotal}
            money={money}
            count={count}
          />
        ))}
      </ul>

      <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
        <div data-role="meta-ads-cap" className="flex flex-col gap-2">
          <Kicker as="span">Spend cap</Kicker>
          {read.spendCap === null ? (
            /* Meta's "0". No cap is a fact, not a limit of zero. */
            <Badge tone="neutral">No spend cap</Badge>
          ) : (
            <>
              <span className="font-mono text-[0.92rem] tabular-nums text-text">
                {money(read.amountSpent)} / {money(read.spendCap)}
              </span>
              {read.amountSpent !== null && (
                /* `bad` only at the cap, which is a real line: Meta stops
                   delivery there. Below it no verdict is drawn, because any
                   threshold short of the cap would be one this product made up. */
                <ProportionBar
                  tone={read.amountSpent >= read.spendCap ? 'bad' : 'neutral'}
                  value={read.amountSpent}
                  total={read.spendCap}
                />
              )}
            </>
          )}
        </div>

        <div data-role="meta-ads-campaign-counts" className="flex flex-col gap-2">
          <Kicker as="span">Campaigns</Kicker>
          <span className="flex flex-wrap gap-2">
            {statusCounts.length === 0 ? (
              /* A real zero: the list was read, and it is empty. */
              <Badge tone="neutral">None</Badge>
            ) : (
              statusCounts.map(([status, n]) => (
                <Badge key={status} tone={STATUS_TONE[status]}>
                  {formatCount(n)} {STATUS_LABEL[status]}
                </Badge>
              ))
            )}
          </span>
        </div>
      </div>
    </section>
  );
}

function PlacementRow({
  label,
  figures,
  spendTotal,
  money,
  count,
}: {
  label: string;
  figures: MetaAdsFigures;
  spendTotal: number | null;
  money: (minor: number | null) => ReactNode;
  count: (value: number | null) => ReactNode;
}) {
  return (
    <li data-role="meta-ads-placement" className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <span className="text-[0.88rem] text-text">{label}</span>
        <span className="flex flex-wrap items-baseline gap-x-4 font-mono text-[0.82rem] tabular-nums text-muted">
          <span>{money(figures.spend)}</span>
          {/* A number, never a bar: placement reach does not add up to the account's. */}
          <span data-role="meta-ads-placement-reach">{count(figures.reach)}</span>
        </span>
      </div>
      {/* Spend is additive, so it is the one figure a bar may compare. */}
      {figures.spend !== null && spendTotal !== null && spendTotal > 0 && (
        <ProportionBar tone="neutral" value={figures.spend} total={spendTotal} />
      )}
    </li>
  );
}

/**
 * One big figure under its label — the account read's, and slice 44's ad
 * surface panels'.
 *
 * Slice 66 drew it as the Blueprint's metric tile: the kicker role over the
 * figure, in a hairline box on a 4% white wash, so the four figures of a read
 * sit as four measurements in a row. A count is sans with tabular digits —
 * mono's dotted zero reads as an 8 at this size — and money stays mono, as the
 * delivery panel's "Spent" does: a currency amount is read digit by digit
 * against an invoice, and mono is what keeps its columns of digits true. The
 * children are a node rather than a number because an absent figure is a
 * "Not measured" chip, never a zero.
 */
export function Figure({
  label,
  kind = 'count',
  children,
}: {
  label: string;
  /** `money` keeps the mono face; a `count` is sans. Default `count`. */
  kind?: 'count' | 'money';
  children: ReactNode;
}) {
  return (
    <div className="rounded-base border border-line bg-white/[0.04] px-3.5 py-3">
      <Kicker as="dt">{label}</Kicker>
      <dd
        className={cn(
          'mt-1.5 text-[1.25rem] leading-none tabular-nums text-text',
          kind === 'money' ? 'font-mono' : 'font-semibold tracking-[-0.02em]',
        )}
      >
        {children}
      </dd>
    </div>
  );
}

/** Campaign counts by status, most first, only statuses that have any. */
function countStatuses(statuses: readonly MetaDeliveryStatus[]): [MetaDeliveryStatus, number][] {
  const counts = new Map<MetaDeliveryStatus, number>();
  for (const status of statuses) counts.set(status, (counts.get(status) ?? 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1]);
}
